import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc, addDoc, updateDoc, serverTimestamp, runTransaction, deleteDoc, setDoc } from "firebase/firestore";
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, Search, Info, Loader2, CheckCircle, FileCheck } from 'lucide-react';
import CreatePickupChallanSkeleton from './components/CreatePickupChallanSkeleton.js';
import PartsConfigTable from './components/PartsConfigTable.js';
import MultiFileUpload from './components/MultiFileUpload.js';
import { getSalesShippingSnapshot, getShippingSnapshotAddress } from './utils/shippingSnapshot.js';
import { useConfig } from './hooks/useConfig.js';
import useClickOutside from './hooks/useClickOutside.js';
import { storage, ref, uploadBytes, getDownloadURL } from './firebaseUtils.js';

const formatAddress = (address) => {
  if (!address) return '';
  if (typeof address === 'string') return address;

  return [
    address.attention,
    address.address,
    address.city,
    address.state,
    address.pincode ? `- ${address.pincode}` : ''
  ].filter(Boolean).join(', ');
};

const getPickupChallanSuffix = (type) => {
  return type === 'Rental Return' ? 'R' : 'S';
};

const buildPickupChallanNo = (inwardNo, type, pickupDate) => {
  if (!inwardNo) return '';

  const baseDate = pickupDate ? new Date(pickupDate) : new Date();
  const safeDate = Number.isNaN(baseDate.getTime()) ? new Date() : baseDate;
  const year = String(safeDate.getFullYear()).slice(-2);
  const month = String(safeDate.getMonth() + 1).padStart(2, '0');

  return `PC/${year}/${month}/${inwardNo}/${getPickupChallanSuffix(type)}`;
};

const getPickupActiveQty = (component = {}) => {
  const materialQty = Number(component.quantity || 0);
  const goodQty = Number(component.liftedGood || 0);
  const damageQty = Number(component.liftedDamage || 0);
  const missingQty = Number(component.finalMissing || 0);

  return Math.max(0, materialQty - goodQty - damageQty - missingQty);
};

const CreatePickupChallan = ({ db, user }) => {
  const { config } = useConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const initialOrderId = location.state?.orderId;
  const initialDispatchId = location.state?.dispatchId;

  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savingAction, setSavingAction] = useState(null); // 'draft' | 'confirm' | 'delete' | null
  const [fetchingDC, setFetchingDC] = useState(false);
  const [deliveredDCs, setDeliveredDCs] = useState([]);
  const [firms, setFirms] = useState([]);

  // Search states
  const [dcSearchTerm, setDcSearchTerm] = useState('');
  const [dcSuggestions, setDcSuggestions] = useState([]);
  const [showDcSuggestions, setShowDcSuggestions] = useState(false);
  const dcSearchRef = useClickOutside(() => setShowDcSuggestions(false));

  const [bomDatabase, setBomDatabase] = useState([]);
  const [compSearchTerm, setCompSearchTerm] = useState('');
  const [compSuggestions, setCompSuggestions] = useState([]);
  const [showCompSuggestions, setShowCompSuggestions] = useState(false);
  const compSearchRef = useClickOutside(() => setShowCompSuggestions(false));
  
  const [fulfillmentMap, setFulfillmentMap] = useState({});

  const calculateFulfillment = async (referenceNo, currentInwardId = null) => {
    try {
      const q = query(collection(db, "inwards"), where("referenceNo", "==", referenceNo));
      const snap = await getDocs(q);
      const map = {};
      snap.docs.forEach(doc => {
        if (doc.id === currentInwardId) return;
        const data = doc.data();
        data.components?.forEach(c => {
          if (!map[c.sku]) map[c.sku] = 0;
          map[c.sku] += Number(c.quantity || 0);
        });
      });
      setFulfillmentMap(map);
      return map;
    } catch (error) {
      console.error("Error calculating fulfillment:", error);
      return {};
    }
  };

  const [formData, setFormData] = useState({
    type: 'Rental Return',
    referenceNo: '',
    firmId: '',
    inwardNo: '',
    pickupAgent: '',
    coordinator: '',
    customerID: '',
    customerName: '',
    billingCustomerName: '',
    billingAddress: '',
    siteCustomerName: '',
    siteProjectName: '',
    siteAddress: '',
    gstin: '',
    salesperson: '',
    orderBy: { name: '', email: '', contact: '' },
    sitePerson: { name: '', email: '', contact: '' },
    remarks: '',
    completionRemarks: '',
    completionFiles: [],
    components: [],
    handedOverBy: { name: '', receivedBy: '', handoverDate: '', handoverTime: '' },
    receivedBy: '',
    receivedDate: '',
    receivedTime: '',
    verifiedBy: '',
    status: 'pc_draft',
    dcDate: '',
    pickupDate: new Date().toISOString().split('T')[0],
    intimationDate: new Date().toISOString().split('T')[0]
  });

  const isConfirmed = formData.status === 'pc_ready' || formData.status === 'pc_completed' || formData.status === 'mic_draft' || formData.status === 'mic_ready' || formData.status === 'mic_completed';
  const [selectedCompletionFile, setSelectedCompletionFile] = useState(null);
  const fieldStaffOptions = config?.field_staff?.length
    ? config.field_staff
    : ['Shakti Singh', 'Govind Suthar', 'Shahid Ali', 'Prashant Dhar', 'Ankit Jangid', 'Deepak Verma', 'Deepak Kapoor', 'Pranav Machhi', 'Ghanshayam Prajapat', 'Rajesh Kumar', 'Ram Chaudhary'];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [firmsSnap, dcSnap, bomSnap] = await Promise.all([
          getDocs(collection(db, "firms")),
          getDocs(query(collection(db, "dispatches"), where("dispatchStatus", "==", "delivered"))),
          getDocs(collection(db, "bom_products"))
        ]);

        setFirms(firmsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        const eligibleDeliveredDCs = dcSnap.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(dc => dc.typeOfMOC !== 'Interbranch Transfer');

        setDeliveredDCs(eligibleDeliveredDCs);
        setBomDatabase(bomSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        if (id) {
          const docSnap = await getDoc(doc(db, "inwards", id));
          if (docSnap.exists()) {
            const data = docSnap.data();
            
            if (data.referenceNo && data.components) {
              const matchingDC = dcSnap.docs.find(d => d.data().challanNo === data.referenceNo || d.data().mocNo === data.referenceNo)?.data();
              const currentFulfillment = await calculateFulfillment(data.referenceNo, id);
              
              data.components = data.components.map(c => {
                const alreadyPicked = currentFulfillment[c.sku] || 0;
                let recoveredDcQty = c.dcQty;
                if (recoveredDcQty === undefined && matchingDC) {
                  const dcComp = matchingDC.components?.find(dc => dc.sku === c.sku);
                  if (dcComp) {
                    recoveredDcQty = Number(dcComp.newQty || 0) + Number(dcComp.rentalQty || 0);
                  }
                }
                return { ...c, dcQty: recoveredDcQty, prevQty: alreadyPicked };
              });
            }
            
            const handedOverBy = data.handedOverBy || {};
            const splitHandoverDateTime = handedOverBy.dateTime || '';
            const splitReceivedDateTime = data.receivedAt || '';

            setFormData(prev => ({
              ...prev,
              ...data,
              handedOverBy: {
                name: handedOverBy.name || '',
                receivedBy: handedOverBy.receivedBy || '',
                handoverDate: handedOverBy.handoverDate || (splitHandoverDateTime ? splitHandoverDateTime.split('T')[0] : ''),
                handoverTime: handedOverBy.handoverTime || (splitHandoverDateTime && splitHandoverDateTime.includes('T') ? splitHandoverDateTime.split('T')[1] : '')
              },
              receivedDate: data.receivedDate || (splitReceivedDateTime ? splitReceivedDateTime.split('T')[0] : ''),
              receivedTime: data.receivedTime || (splitReceivedDateTime && splitReceivedDateTime.includes('T') ? splitReceivedDateTime.split('T')[1] : ''),
              completionRemarks: data.completionRemarks || '',
              completionFiles: data.completionFiles || []
            }));
            setDcSearchTerm(data.referenceNo || '');
          }
        } else {
            const dcList = eligibleDeliveredDCs;

            if (initialDispatchId) {
              const selectedDispatch = dcList.find(d => d.id === initialDispatchId);
              if (selectedDispatch) await handleDCChange(selectedDispatch);
            } else if (initialOrderId) {
              const orderDc = dcList.find(d => d.orderId === initialOrderId);
              if (orderDc) await handleDCChange(orderDc);
            }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [db, id, initialDispatchId, initialOrderId]);

  const handleDcSearch = (val) => {
    setDcSearchTerm(val);
    if (val.length > 1) {
      const term = val.toLowerCase();
      const matches = deliveredDCs.filter(dc => 
        (dc.challanNo || dc.mocNo || '').toLowerCase().includes(term) ||
        (dc.customerName || '').toLowerCase().includes(term) ||
        (dc.destinationWarehouseName || '').toLowerCase().includes(term)
      ).slice(0, 10);
      setDcSuggestions(matches);
      setShowDcSuggestions(true);
    } else {
      setShowDcSuggestions(false);
    }
  };

  const handleDCChange = async (dcDoc) => {
    setFetchingDC(true);
    setShowDcSuggestions(false);
    const referenceNo = dcDoc.challanNo || dcDoc.mocNo;
    setDcSearchTerm(referenceNo);
    
    try {
      const [orderSnap, currentFulfillment] = await Promise.all([
        dcDoc.orderId ? getDoc(doc(db, "orders", dcDoc.orderId)) : Promise.resolve(null),
        calculateFulfillment(referenceNo)
      ]);
      const orderData = orderSnap?.exists?.() ? orderSnap.data() : {};

      let customerData = {};
      if (orderData.customerID) {
        const custSnap = await getDoc(doc(db, "customers", orderData.customerID));
        if (custSnap.exists()) customerData = custSnap.data();
      }

      const billingAddr = orderData.billingAddress || dcDoc.billingAddress;
      const shippingSnapshot = dcDoc.typeOfMOC !== 'Interbranch Transfer'
        ? getSalesShippingSnapshot(dcDoc, orderData)
        : null;
      const shippingAddr = getShippingSnapshotAddress(shippingSnapshot, dcDoc.shipToAddress || dcDoc.shippingAddress || formatAddress(orderData.shippingAddress));
      const billingAddrText = formatAddress(billingAddr);
      const shippingAddrText = formatAddress(shippingAddr);
      const billingCustomerName = orderData.customerName || dcDoc.billToCustomer || dcDoc.customerName || '';
      const billingContactName = orderData.primary_contact?.name || orderData.billingAddress?.attention || dcDoc.orderBy || '';
      const billingContactEmail = orderData.primary_contact?.email || orderData.billingAddress?.email || dcDoc.billToEmail || customerData.email || '';
      const billingContactPhone = orderData.primary_contact?.phone || orderData.billingAddress?.phone || dcDoc.orderByContact || '';
      const siteCustomerName = shippingSnapshot?.customerName || dcDoc.shipToCustomer || dcDoc.customerName || '';
      const siteProjectName = dcDoc.projectName || orderData.projectName || '';
      const sitePersonName = shippingSnapshot?.contactName || dcDoc.sitePerson || dcDoc.destinationContactName || dcDoc.driverName || '';
      const sitePersonEmail = shippingSnapshot?.contactEmail || dcDoc.shipToEmail || dcDoc.destinationContactEmail || '';
      const sitePersonContact = shippingSnapshot?.contactPhone || dcDoc.sitePersonContact || dcDoc.destinationContactPhone || '';
      const inwardType = dcDoc.typeOfMOC === 'Rental' ? 'Rental Return' : 'Sale Return';

      setFormData(prev => ({
        ...prev,
        orderId: dcDoc.orderId || prev.orderId || '',
        type: inwardType,
        referenceNo: referenceNo,
        dcDate: dcDoc.dispatchDate || 'N/A',
        firmId: dcDoc.firmId || orderData.firmId || '',
        customerID: orderData.customerID || dcDoc.customerID || '',
        customerName: dcDoc.customerName || orderData.customerName || '',
        billingCustomerName,
        billingAddress: billingAddrText,
        siteCustomerName,
        siteProjectName,
        siteAddress: shippingAddrText,
        gstin: orderData.billToGstin || customerData.gstin || customerData.gst_number || dcDoc.billToGstin || '',
        salesperson: orderData.salesPerson || '',
        orderBy: {
          name: billingContactName,
          email: billingContactEmail,
          contact: billingContactPhone
        },
        sitePerson: {
          name: sitePersonName,
          email: sitePersonEmail,
          contact: sitePersonContact
        },
        components: (dcDoc.components || []).map(comp => {
          const totalDCQty = Number(comp.newQty || 0) + Number(comp.rentalQty || 0);
          const alreadyPicked = currentFulfillment[comp.sku] || 0;
          const balance = Math.max(0, totalDCQty - alreadyPicked);
          
          return {
            ...comp,
            quantity: balance,
            dcQty: totalDCQty,
            prevQty: alreadyPicked,
            liftedGood: 0,
            liftedDamage: 0,
            totalLifted: 0,
            totalActive: balance,
            finalMissing: 0
          };
        }).filter(c => c.quantity > 0)
      }));
    } catch (error) {
      console.error("Error prefilling:", error);
    } finally {
      setFetchingDC(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNestedInputChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const handleComponentChange = (index, field, value) => {
    const updatedComponents = [...formData.components];
    const comp = updatedComponents[index];

    const toNum = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };

    // Apply incoming value
    let nextVal = Math.max(0, toNum(value));

    // Restrict Material Qty to balance: dcQty - accountedInOtherPCs
    if (field === 'quantity' && comp.dcQty !== undefined) {
      const alreadyPicked = Number(comp.prevQty || 0);
      const totalDCQty = Number(comp.dcQty);
      const balanceAvailable = Math.max(0, totalDCQty - alreadyPicked);
      
      if (nextVal > balanceAvailable) {
        alert(`Cannot exceed available balance (${balanceAvailable}).\n\nTotal delivered in DC: ${totalDCQty}\nAlready picked up in other PCs: ${alreadyPicked}`);
        nextVal = balanceAvailable;
      }
    }

    if (field === 'quantity') {
      const otherOutcomeTotal =
        Number(comp.liftedGood || 0) +
        Number(comp.liftedDamage || 0) +
        Number(comp.finalMissing || 0);

      if (nextVal < otherOutcomeTotal) {
        alert(`Material Qty cannot be less than the sum of Good, Damage, and Missing (${otherOutcomeTotal}).`);
        nextVal = otherOutcomeTotal;
      }
    }

    if (['liftedGood', 'liftedDamage', 'finalMissing'].includes(field)) {
      const nextGood = field === 'liftedGood' ? nextVal : Number(comp.liftedGood || 0);
      const nextDamage = field === 'liftedDamage' ? nextVal : Number(comp.liftedDamage || 0);
      const nextMissing = field === 'finalMissing' ? nextVal : Number(comp.finalMissing || 0);
      const materialQty = Number(comp.quantity || 0);
      const totalOutcome = nextGood + nextDamage + nextMissing;

      if (totalOutcome > materialQty) {
        alert(`Good + Damage + Missing cannot exceed Material Qty (${materialQty}).`);
        return;
      }
    }
    
    comp[field] = nextVal;

    comp.totalLifted = toNum(comp.liftedGood) + toNum(comp.liftedDamage);
    comp.totalActive = getPickupActiveQty(comp);
    setFormData(prev => ({ ...prev, components: updatedComponents }));
  };

  const deleteComponent = (index) => {
    const updated = formData.components.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, components: updated }));
  };

  const handleCompSearch = (val) => {
    setCompSearchTerm(val);
    if (val.length > 1) {
      const term = val.toLowerCase();
      const matches = bomDatabase.filter(p => 
        (p.name || '').toLowerCase().includes(term) ||
        (p.sku || '').toLowerCase().includes(term)
      ).slice(0, 10);
      setCompSuggestions(matches);
      setShowCompSuggestions(true);
    } else {
      setShowCompSuggestions(false);
    }
  };

  const addComponent = (product) => {
    const existingIndex = formData.components.findIndex(c => c.sku === product.sku);
    if (existingIndex > -1) {
      handleComponentChange(existingIndex, 'quantity', Number(formData.components[existingIndex].quantity || 0) + 1);
      return;
    }

    const matchingDC = deliveredDCs.find(dc => (dc.challanNo || dc.mocNo) === formData.referenceNo);
    let dcQty = undefined;
    if (matchingDC) {
      const dcComp = matchingDC.components?.find(c => c.sku === product.sku);
      if (dcComp) {
        dcQty = Number(dcComp.newQty || 0) + Number(dcComp.rentalQty || 0);
      }
    }

    const alreadyPicked = fulfillmentMap[product.sku] || 0;
    const balance = dcQty !== undefined ? Math.max(0, dcQty - alreadyPicked) : 1;

    if (dcQty !== undefined && balance <= 0) {
      alert(`Component ${product.sku} is already fully accounted for in this DC.`);
      return;
    }

    const newComp = { ...product, quantity: balance, dcQty: dcQty, prevQty: alreadyPicked, liftedGood: 0, liftedDamage: 0, totalLifted: 0, totalActive: balance, finalMissing: 0 };
    const updated = [...formData.components];
    updated.push(newComp);
    setFormData(prev => ({ ...prev, components: updated }));
    setCompSearchTerm('');
    setShowCompSuggestions(false);
  };

  const getNextInwardNumber = async () => {
    const sequencesRef = doc(db, "counters", "inward_sequence");
    return await runTransaction(db, async (transaction) => {
      const sequenceDoc = await transaction.get(sequencesRef);
      let currentCount = 2000; 
      if (sequenceDoc.exists() && sequenceDoc.data().count !== undefined) {
        currentCount = sequenceDoc.data().count;
      }
      const nextCount = currentCount + 1;
      transaction.set(sequencesRef, { count: nextCount }, { merge: true });
      return currentCount.toString();
    });
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm("Are you sure you want to delete this Pickup? This action cannot be undone.")) return;
    
    setSavingAction('delete');
    setIsSaving(true);
    try {
      await deleteDoc(doc(db, "inwards", id));
      alert("Pickup deleted successfully.");
      navigate('/ops/pickup-challan');
    } catch (error) {
      console.error("Error deleting pickup:", error);
      alert("Error: " + error.message);
    } finally {
      setIsSaving(false);
      setSavingAction(null);
    }
  };

  const handleSave = async (status) => {
    if (status === 'pc_completed') {
      const hasExistingCompletionFile = (formData.completionFiles || []).length > 0;
      if (!selectedCompletionFile && !hasExistingCompletionFile) {
        alert("Please upload the scanned pickup challan before confirming.");
        return;
      }

      if (!window.confirm("Confirm this Pickup Challan? Please double-check all quantities, remarks, and the scanned pickup challan before continuing. After confirmation, this record will be locked for editing and a new draft Pickup Challan will be created for any remaining active quantity.")) {
        return;
      }
    }
    
    setSavingAction(status === 'pc_completed' ? 'confirm' : 'draft');
    setIsSaving(true);
    try {
      let finalInwardNo = formData.inwardNo;
      if (!id && !finalInwardNo) {
        finalInwardNo = await getNextInwardNumber();
      }
      const finalPickupChallanNo = buildPickupChallanNo(finalInwardNo, formData.type, formData.pickupDate);

      const finalComponents = formData.components.map(c => ({
        ...c,
        totalLifted: (Number(c.liftedGood) || 0) + (Number(c.liftedDamage) || 0),
        totalActive: getPickupActiveQty(c)
      }));

      let completionFiles = formData.completionFiles || [];
      const shouldUploadCompletionFile = !!selectedCompletionFile;
      if (shouldUploadCompletionFile) {
        const safeName = (selectedCompletionFile.name || 'file').replace(/[^\w.\-() ]+/g, '_');
        const path = `pc_completion/${id || finalInwardNo || Date.now()}/${Date.now()}_${safeName}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, selectedCompletionFile);
        const url = await getDownloadURL(storageRef);
        completionFiles = [{
          name: selectedCompletionFile.name,
          type: selectedCompletionFile.type,
          size: selectedCompletionFile.size,
          url
        }];
      }

      const payload = {
        ...formData,
        handedOverBy: {
          ...formData.handedOverBy,
          dateTime: [formData.handedOverBy?.handoverDate || '', formData.handedOverBy?.handoverTime || ''].filter(Boolean).join('T')
        },
        receivedAt: [formData.receivedDate || '', formData.receivedTime || ''].filter(Boolean).join('T'),
        components: finalComponents,
        inwardNo: finalInwardNo,
        pickupChallanNo: finalPickupChallanNo,
        status: status,
        completionRemarks: formData.completionRemarks || '',
        completionFiles,
        updatedAt: serverTimestamp(),
        updatedBy: user.displayName || user.email
      };

      if (status === 'pc_completed') {
        payload.completedAt = serverTimestamp();
      }

      let savedDocId = id || null;
      if (id) {
        await updateDoc(doc(db, "inwards", id), payload);
      } else {
        const rootId = doc(collection(db, "inwards")).id;
        const docRef = await addDoc(collection(db, "inwards"), {
          ...payload,
          rootInwardId: rootId,
          createdAt: serverTimestamp(),
          createdBy: user.displayName || user.email
        });
        savedDocId = docRef.id;
      }
      
      // --- AUTO-CREATE DRAFT FOR REMAINING QUANTITIES ---
      if (status === 'pc_completed') {
        const remainingComponents = finalComponents
          .map(c => {
            const balance = Number(c.totalActive) || 0;
            
            if (balance > 0) {
              return {
                ...c,
                quantity: balance,
                dcQty: c.dcQty,
                liftedGood: 0,
                liftedDamage: 0,
                totalLifted: 0,
                totalActive: balance,
                finalMissing: 0
              };
            }
            return null;
          })
          .filter(c => c !== null);

        if (remainingComponents.length > 0) {
          const nextInwardNo = await getNextInwardNumber();
          const nextPickupDate = new Date().toISOString().split('T')[0];
          const draftPayload = {
            rootInwardId: formData.rootInwardId || (savedDocId ? savedDocId : 'unknown'),
            orderId: formData.orderId || '',
            type: formData.type,
            referenceNo: formData.referenceNo,
            firmId: formData.firmId,
            inwardNo: nextInwardNo,
            pickupChallanNo: buildPickupChallanNo(nextInwardNo, formData.type, nextPickupDate),
            pickupAgent: formData.pickupAgent,
            coordinator: formData.coordinator,
            customerID: formData.customerID,
            customerName: formData.customerName,
            billingCustomerName: formData.billingCustomerName,
            billingAddress: formData.billingAddress,
            siteCustomerName: formData.siteCustomerName,
            siteProjectName: formData.siteProjectName,
            siteAddress: formData.siteAddress,
            gstin: formData.gstin,
            salesperson: formData.salesperson,
            orderBy: formData.orderBy,
            sitePerson: formData.sitePerson,
            remarks: formData.remarks,
            completionRemarks: '',
            completionFiles: [],
            components: remainingComponents,
            handedOverBy: formData.handedOverBy,
            status: 'pc_draft',
            dcDate: formData.dcDate,
            pickupDate: nextPickupDate,
            intimationDate: new Date().toISOString().split('T')[0],
            createdAt: serverTimestamp(),
            createdBy: user.displayName || user.email,
            updatedAt: serverTimestamp(),
            updatedBy: user.displayName || user.email
          };
          await addDoc(collection(db, "inwards"), draftPayload);
          console.log("Auto-created draft PC for remaining balance:", nextInwardNo);
        }
      }
      
      alert(`Pickup ${status === 'pc_draft' ? 'Saved as Draft' : 'Completed'} Successfully.`);
      // After save/confirm, go to PC Detail view
      if (savedDocId) navigate(`/ops/pickup-challan/${savedDocId}`);
      else navigate('/ops/pickup-challan');
    } catch (error) {
      alert("Error: " + error.message);
    } finally {
      if (selectedCompletionFile) {
        setSelectedCompletionFile(null);
      }
      setIsSaving(false);
      setSavingAction(null);
    }
  };

  const inputStyle = { width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', outline: 'none', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' };
  const fieldGroupStyle = { display: 'flex', flexDirection: 'column', gap: '5px' };
  const sectionHeaderStyle = { fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '20px', paddingBottom: '10px', borderBottom: '2px solid #e2e8f0', marginTop: '30px' };
  const confirmBtnStyle = { padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', border: 'none', backgroundColor: '#059669', color: 'white' };

  if (loading) return <CreatePickupChallanSkeleton />;

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <button onClick={() => navigate(id ? `/ops/pickup-challan/${id}` : '/ops/pickup-challan')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', color: '#64748b' }}>
          <ArrowLeft size={20} /> Back
        </button>
        <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '800' }}>{id ? 'Edit' : 'Initiate'} Pickup</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          {!isConfirmed && (
            <>
              <button
                onClick={() => handleSave('pc_draft')}
                disabled={isSaving}
                style={{ backgroundColor: 'white', border: '1px solid #d1d5db', padding: '10px 20px', borderRadius: '8px', fontWeight: '700', cursor: isSaving ? 'not-allowed' : 'pointer', minWidth: '130px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              >
                {isSaving && savingAction === 'draft' ? <Loader2 size={18} className="animate-spin" /> : null}
                {isSaving && savingAction === 'draft' ? 'Saving…' : 'Save Draft'}
              </button>
              <button 
                onClick={() => handleSave('pc_completed')} 
                disabled={isSaving} 
                style={{ ...confirmBtnStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', minWidth: '150px', cursor: isSaving ? 'not-allowed' : confirmBtnStyle.cursor }}
              >
                {isSaving && savingAction === 'confirm' ? <Loader2 size={18} className="animate-spin" /> : <FileCheck size={18} />}
                {isSaving && savingAction === 'confirm' ? 'Confirming…' : 'Confirm PC'}
              </button>
              {id && (
                <button 
                  onClick={handleDelete} 
                  disabled={isSaving} 
                  style={{ backgroundColor: 'white', color: '#dc2626', border: '1px solid #fecaca', padding: '10px 20px', borderRadius: '8px', fontWeight: '700', cursor: isSaving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', minWidth: '120px' }}
                >
                  {isSaving && savingAction === 'delete' ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
                  {isSaving && savingAction === 'delete' ? 'Deleting…' : 'Delete'}
                </button>
              )}
            </>
          )}
          {isConfirmed && (
             <div style={{ backgroundColor: '#f0fdf4', color: '#166534', padding: '10px 20px', borderRadius: '8px', border: '1px solid #bbf7d0', fontWeight: 'bold' }}>
                {formData.status === 'pc_ready' ? 'PC READY' : 
                 formData.status === 'pc_completed' ? 'PICKUP COMPLETED' : 
                 formData.status.replace('_', ' ').toUpperCase()}
             </div>
          )}
        </div>
      </div>

      <form style={{ backgroundColor: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }} onSubmit={(e) => e.preventDefault()}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginBottom: '30px' }}>
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Inward Type</label>
            <select style={inputStyle} value={formData.type} onChange={(e) => handleInputChange('type', e.target.value)} disabled={isConfirmed}>
              <option value="Rental Return">Rental Return</option>
              <option value="Sale Return">Sale Return</option>
            </select>
          </div>

          <div style={{ position: 'relative', ...fieldGroupStyle }} ref={dcSearchRef}>
            <label style={labelStyle}>Delivered DC Picker</label>
            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #d1d5db', borderRadius: '6px', padding: '0 10px', height: '42px', backgroundColor: 'transparent' }}>
              {fetchingDC ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b', fontSize: '13px' }}>
                  <Loader2 size={18} className="animate-spin" />
                  <span>Fetching data...</span>
                </div>
              ) : (
                <>
                  <Search size={18} color="#64748b" />
                  <input type="text" placeholder="Search DC..." value={dcSearchTerm} onChange={(e) => handleDcSearch(e.target.value)} onFocus={() => dcSearchTerm.length > 1 && !isConfirmed && setShowDcSuggestions(true)} style={{ border: 'none', padding: '10px', outline: 'none', width: '100%', fontSize: '14px', boxSizing: 'border-box', flex: 1, minWidth: 0 }} readOnly={isConfirmed} />
                </>
              )}
            </div>

            {showDcSuggestions && !isConfirmed && (
              <div style={{ position: 'absolute', top: '100%', left: 0, width: '100%', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px', zIndex: 10, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', maxHeight: '250px', overflowY: 'auto' }}>
                {dcSuggestions.length > 0 ? (
                  dcSuggestions.map(dc => (
                    <div key={dc.id} onClick={() => handleDCChange(dc)} style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: '13px' }}>
                      <strong>{dc.challanNo || dc.mocNo}</strong> - {dc.customerName}
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '15px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>
                    No Delivered DC found matching "{dcSearchTerm}"
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div style={sectionHeaderStyle}>Basic Details</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
          <div style={fieldGroupStyle}><label style={labelStyle}>Firm Picker</label><select style={inputStyle} value={formData.firmId} onChange={(e) => handleInputChange('firmId', e.target.value)} disabled={isConfirmed}><option value="">-- Select --</option>{firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}</select></div>
          <div style={fieldGroupStyle}><label style={labelStyle}>Inward ID</label><input type="text" style={{ ...inputStyle, backgroundColor: '#f8fafc' }} value={formData.inwardNo || 'Auto-generated'} readOnly /></div>
          <div style={fieldGroupStyle}><label style={labelStyle}>Intimation Date</label><input type="date" style={inputStyle} value={formData.intimationDate} onChange={(e) => handleInputChange('intimationDate', e.target.value)} readOnly={isConfirmed} /></div>
          <div style={fieldGroupStyle}><label style={labelStyle}>Pickup Date</label><input type="date" style={inputStyle} value={formData.pickupDate} onChange={(e) => handleInputChange('pickupDate', e.target.value)} readOnly={isConfirmed} /></div>
          <div style={{ gridColumn: 'span 2', ...fieldGroupStyle }}>
            <label style={labelStyle}>Pickup Agent</label>
            <select style={inputStyle} value={formData.pickupAgent} onChange={(e) => handleInputChange('pickupAgent', e.target.value)} disabled={isConfirmed}>
              <option value="">-- Select Agent --</option>
              {fieldStaffOptions.map((name) => (<option key={name} value={name}>{name}</option>))}
            </select>
          </div>
          <div style={{ gridColumn: 'span 2', ...fieldGroupStyle }}>
            <label style={labelStyle}>Coordinator</label>
            <select style={inputStyle} value={formData.coordinator} onChange={(e) => handleInputChange('coordinator', e.target.value)} disabled={isConfirmed}>
              <option value="">-- Select Coordinator --</option>
              {fieldStaffOptions.map((name) => (<option key={name} value={name}>{name}</option>))}
            </select>
          </div>
        </div>

        <div style={sectionHeaderStyle}>Party Details</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', padding: '20px', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
            <div style={{ fontWeight: '700', fontSize: '15px', color: '#1e293b', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px', marginBottom: '5px' }}>Consignee Details (Billing)</div>
            <div style={fieldGroupStyle}><label style={labelStyle}>Billing Customer</label><input type="text" style={inputStyle} value={formData.billingCustomerName} onChange={(e) => handleInputChange('billingCustomerName', e.target.value)} readOnly={isConfirmed} /></div>
            <div style={fieldGroupStyle}><label style={labelStyle}>Billing Address</label><textarea style={{ ...inputStyle, height: '80px' }} value={formData.billingAddress} onChange={(e) => handleInputChange('billingAddress', e.target.value)} readOnly={isConfirmed} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div style={fieldGroupStyle}><label style={labelStyle}>Contact Name</label><input type="text" style={inputStyle} value={formData.orderBy?.name} onChange={(e) => handleNestedInputChange('orderBy', 'name', e.target.value)} readOnly={isConfirmed} /></div>
              <div style={fieldGroupStyle}><label style={labelStyle}>Contact Number</label><input type="text" style={inputStyle} value={formData.orderBy?.contact} onChange={(e) => handleNestedInputChange('orderBy', 'contact', e.target.value)} readOnly={isConfirmed} /></div>
            </div>
            <div style={fieldGroupStyle}><label style={labelStyle}>Contact Email</label><input type="email" style={inputStyle} value={formData.orderBy?.email} onChange={(e) => handleNestedInputChange('orderBy', 'email', e.target.value)} readOnly={isConfirmed} /></div>
            <div style={fieldGroupStyle}><label style={labelStyle}>GSTIN/UIN</label><input type="text" style={inputStyle} value={formData.gstin} onChange={(e) => handleInputChange('gstin', e.target.value)} readOnly={isConfirmed} /></div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', backgroundColor: '#f8fafc', padding: '20px', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
            <div style={{ fontWeight: '700', fontSize: '15px', color: '#2563eb', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px', marginBottom: '5px' }}>Pickup Location (Site)</div>
            <div style={fieldGroupStyle}><label style={labelStyle}>Site Customer</label><input type="text" style={inputStyle} value={formData.siteCustomerName} onChange={(e) => handleInputChange('siteCustomerName', e.target.value)} readOnly={isConfirmed} /></div>
            <div style={fieldGroupStyle}><label style={labelStyle}>Project Name</label><input type="text" style={inputStyle} value={formData.siteProjectName} onChange={(e) => handleInputChange('siteProjectName', e.target.value)} readOnly={isConfirmed} /></div>
            <div style={fieldGroupStyle}><label style={labelStyle}>Customer Ship Address</label><textarea style={{ ...inputStyle, height: '80px' }} value={formData.siteAddress} onChange={(e) => handleInputChange('siteAddress', e.target.value)} readOnly={isConfirmed} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div style={fieldGroupStyle}><label style={labelStyle}>Site Person</label><input type="text" style={inputStyle} value={formData.sitePerson?.name} onChange={(e) => handleNestedInputChange('sitePerson', 'name', e.target.value)} readOnly={isConfirmed} /></div>
              <div style={fieldGroupStyle}><label style={labelStyle}>Site Contact</label><input type="text" style={inputStyle} value={formData.sitePerson?.contact} onChange={(e) => handleNestedInputChange('sitePerson', 'contact', e.target.value)} readOnly={isConfirmed} /></div>
            </div>
            <div style={fieldGroupStyle}><label style={labelStyle}>Site Email</label><input type="email" style={inputStyle} value={formData.sitePerson?.email} onChange={(e) => handleNestedInputChange('sitePerson', 'email', e.target.value)} readOnly={isConfirmed} /></div>
            <div style={fieldGroupStyle}><label style={labelStyle}>Salesperson</label><input type="text" style={inputStyle} value={formData.salesperson} onChange={(e) => handleInputChange('salesperson', e.target.value)} readOnly={isConfirmed} /></div>
          </div>
        </div>

        <div style={{ marginTop: '20px', ...fieldGroupStyle }}><label style={labelStyle}>General Remarks</label><textarea style={{ ...inputStyle, height: '80px' }} value={formData.remarks} onChange={(e) => handleInputChange('remarks', e.target.value)} readOnly={isConfirmed} /></div>

        <div style={sectionHeaderStyle}>Components</div>
        {!isConfirmed && (
          <div style={{ position: 'relative', width: '100%', marginBottom: '15px' }} ref={compSearchRef}>
            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#f3f4f6', padding: '8px 12px', borderRadius: '6px' }}>
              <Search size={16} color="#64748b" />
              <input
                type="text"
                placeholder="Add components..."
                value={compSearchTerm}
                onChange={(e) => handleCompSearch(e.target.value)}
                onFocus={() => compSearchTerm.length > 1 && setShowCompSuggestions(true)}
                style={{ border: 'none', background: 'transparent', marginLeft: '8px', outline: 'none', width: '100%', fontSize: '13px', boxSizing: 'border-box', flex: 1, minWidth: 0 }}
              />
            </div>
            {showCompSuggestions && (
              <div style={{ position: 'absolute', top: '100%', left: 0, width: '100%', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px', zIndex: 10, maxHeight: '250px', overflowY: 'auto' }}>
                {compSuggestions.map(p => (<div key={p.id} onClick={() => addComponent(p)} style={{ padding: '10px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}><strong>{p.sku}</strong> - {p.name}</div>))}
              </div>
            )}
          </div>
        )}
        
        <PartsConfigTable
          mode="pickup"
          components={formData.components}
          onChange={handleComponentChange}
          onDelete={deleteComponent}
          hideActions={isConfirmed}
          pickupMaterialQtyReadOnly={true}
        />

        <div style={sectionHeaderStyle}>Site Handover Details</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '20px' }}>
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Handed Over By</label>
            <input
              type="text"
              style={inputStyle}
              value={formData.handedOverBy?.name || ''}
              onChange={(e) => handleNestedInputChange('handedOverBy', 'name', e.target.value)}
              readOnly={isConfirmed}
            />
          </div>
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Received By</label>
            <select
              style={inputStyle}
              value={formData.handedOverBy?.receivedBy || ''}
              onChange={(e) => handleNestedInputChange('handedOverBy', 'receivedBy', e.target.value)}
              disabled={isConfirmed}
            >
              <option value="">-- Select --</option>
              {fieldStaffOptions.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Date of Handover</label>
            <input
              type="date"
              style={inputStyle}
              value={formData.handedOverBy?.handoverDate || ''}
              onChange={(e) => handleNestedInputChange('handedOverBy', 'handoverDate', e.target.value)}
              readOnly={isConfirmed}
            />
          </div>
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Time of Handover</label>
            <input
              type="time"
              style={inputStyle}
              value={formData.handedOverBy?.handoverTime || ''}
              onChange={(e) => handleNestedInputChange('handedOverBy', 'handoverTime', e.target.value)}
              readOnly={isConfirmed}
            />
          </div>
        </div>

        <div style={sectionHeaderStyle}>Warehouse Receipt</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '20px' }}>
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Received at Warehouse By</label>
            <select
              style={inputStyle}
              value={formData.receivedBy || ''}
              onChange={(e) => handleInputChange('receivedBy', e.target.value)}
              disabled={isConfirmed}
            >
              <option value="">-- Select --</option>
              {fieldStaffOptions.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Date</label>
            <input
              type="date"
              style={inputStyle}
              value={formData.receivedDate || ''}
              onChange={(e) => handleInputChange('receivedDate', e.target.value)}
              readOnly={isConfirmed}
            />
          </div>
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Time</label>
            <input
              type="time"
              style={inputStyle}
              value={formData.receivedTime || ''}
              onChange={(e) => handleInputChange('receivedTime', e.target.value)}
              readOnly={isConfirmed}
            />
          </div>
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Verified & Checked By</label>
            <select
              style={inputStyle}
              value={formData.verifiedBy || ''}
              onChange={(e) => handleInputChange('verifiedBy', e.target.value)}
              disabled={isConfirmed}
            >
              <option value="">-- Select --</option>
              {fieldStaffOptions.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={sectionHeaderStyle}>Pickup Closeout</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', alignItems: 'start' }}>
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Scanned Pickup Challan</label>
            <MultiFileUpload
              value={selectedCompletionFile ? [selectedCompletionFile] : (formData.completionFiles || [])}
              onFilesChange={(files) => {
                const nextFile = files[0] || null;
                setSelectedCompletionFile(nextFile instanceof File ? nextFile : null);
                if (files.length === 0) {
                  setFormData((prev) => ({ ...prev, completionFiles: [] }));
                }
              }}
              multiple={false}
              maxFiles={1}
              disabled={isConfirmed}
              uploading={isSaving && savingAction === 'confirm'}
              helperText="Single file only. Upload is required before confirming the Pickup Challan."
              allowedTypes={['image/jpeg', 'image/png', 'image/webp', 'application/pdf']}
            />
            {!isConfirmed && (
              <div style={{ fontSize: '12px', color: '#b45309' }}>
                This upload is mandatory before confirming the Pickup Challan.
              </div>
            )}
          </div>
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Pickup Outcome Remarks</label>
            <textarea
              style={{ ...inputStyle, height: '100px' }}
              value={formData.completionRemarks || ''}
              onChange={(e) => handleInputChange('completionRemarks', e.target.value)}
              readOnly={isConfirmed}
              placeholder="Add any site pickup notes, shortages, or observations..."
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreatePickupChallan;
